/**
 * Tiny fetch wrapper for the Event OS frontend.
 *
 * Why a wrapper at all:
 *   - Consistent JSON parsing + Problem-Details (RFC 7807) error handling.
 *   - One place to attach the Cognito id_token (the wrangler-backed
 *     `/api/auth/exchange` exchange sets an HttpOnly cookie, so `credentials:
 *     "include"` is all we need on the browser side).
 *   - Idempotency-Key helper for mutating requests (matches the server's
 *     contract on /api/partners/signup, /api/payments/*, etc.).
 *
 * NOTE: we deliberately do NOT pull in axios. fetch + AbortController + tiny
 * type-narrowing is enough and ships zero bytes of polyfill.
 */

export class ApiError extends Error {
  status: number;
  problem: ProblemDetails;
  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

/* ----------------------------------------------------------------------------
 * Bearer-token getter.
 *
 * F1 stores access + id tokens in-memory in <AuthProvider> (not in a cookie —
 * only the refresh token sits in a __Host-tot_refresh HttpOnly cookie).
 *
 * The AuthProvider registers a getter here on mount via `setBearerTokenGetter`.
 * Every `api()` call asks for the current token and, if present, sets the
 * `Authorization: Bearer …` header. This breaks the circular dep that would
 * happen if api() imported useAuth() directly (api is used by useAuth itself).
 * ---------------------------------------------------------------------------- */
let bearerTokenGetter: () => string | null = () => null;

export function setBearerTokenGetter(fn: () => string | null): void {
  bearerTokenGetter = fn;
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  request_id?: string;
  fields?: Array<{ path: string; code: string }>;
}

interface ApiOptions extends Omit<RequestInit, "body" | "method"> {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  json?: unknown;
  idempotencyKey?: string;
  turnstileToken?: string;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { method = "GET", json, idempotencyKey, turnstileToken, headers, ...rest } = opts;

  // Pull the current in-memory Bearer token (id_token by default — see
  // setBearerTokenGetter above). Null when not authenticated; we just don't
  // send the header in that case.
  const bearer = bearerTokenGetter();

  const init: RequestInit = {
    ...rest,
    method,
    credentials: "include",
    headers: {
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      ...(turnstileToken ? { "CF-Turnstile-Response": turnstileToken } : {}),
      ...(headers ?? {}),
    },
    body: json !== undefined ? JSON.stringify(json) : undefined,
  };

  const r = await fetch(path, init);

  // 204 No Content — common for logout / DELETE.
  if (r.status === 204) return undefined as T;

  const ctype = r.headers.get("Content-Type") ?? "";
  const isJson = ctype.includes("application/json") || ctype.includes("application/problem+json");
  const payload = isJson ? await r.json().catch(() => ({})) : await r.text();

  if (!r.ok) {
    const problem: ProblemDetails =
      isJson && typeof payload === "object" && payload !== null
        ? (payload as ProblemDetails)
        : { status: r.status, detail: typeof payload === "string" ? payload : undefined };
    throw new ApiError(r.status, problem);
  }

  return payload as T;
}

/**
 * RFC 4122 v4 UUID — for client-supplied Idempotency-Keys.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
